import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Fade
} from '@mui/material';
// MUI 7's Grid ignores item/xs/sm/md, so these layouts collapsed to content width
// (dashboard on phones, ST-20261007-0001 follow-up). GridLegacy is the pre-v7 grid.
import Grid from '@mui/material/GridLegacy';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import ErrorBoundary from '../shared/ErrorBoundary';
import { dashboardTheme } from './styles/dashboardTheme';
import { useSocket } from '../../context/SocketContext';

// Import dashboard widgets
import CompactCheckInWidget from './widgets/CompactCheckInWidget';
import QuickActionsGrid from './widgets/QuickActionsGrid';
import ActivityFeed from './widgets/ActivityFeed';
import WeeklyProgress from './widgets/WeeklyProgress';
import GoalsSummary from './widgets/GoalsSummary';
import CheckInHistoryWidget from './widgets/CheckInHistoryWidget';
import SessionSummaryWidget from './widgets/SessionSummaryWidget';

// Import dialogs
import CheckInDialog from './dialogs/CheckInDialog';
import GoalsDialog from './dialogs/GoalsDialog';
import ProgressDialog from './dialogs/ProgressDialog';
import SessionSummaryDialog from './dialogs/SessionSummaryDialog';
import CheckInHistoryDialog from './dialogs/CheckInHistoryDialog';
import OnboardingDialog from './dialogs/OnboardingDialog';

/**
 * DashboardV2 Component
 * New dashboard interface providing overview of all features
 * Only visible when NEW_DASHBOARD feature flag is enabled
 */
// A desktop column of cards; on phones it dissolves so its cards join the single list.
const dashboardColumnSx = {
  display: { xs: 'contents', md: 'flex' },
  flexDirection: 'column',
  gap: 2,
  minWidth: 0
};

const DashboardV2 = ({ user, token }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const socket = useSocket();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Dashboard data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [goalsDialogOpen, setGoalsDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [sessionSummaryDialogOpen, setSessionSummaryDialogOpen] = useState(false);
  const [checkInHistoryDialogOpen, setCheckInHistoryDialogOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    lastCheckIn: null,
    recentActivity: [],
    goals: [],
    weeklyProgress: null,
    checkIns: []
  });

  // Combine recent activity from multiple sources
  const combineRecentActivity = (checkIns, goals, journals, limit = 5) => {
    const activities = [];
    
    // Take more items from each source for the expanded view
    const itemsPerSource = limit > 5 ? 10 : 3;

    // Add recent check-ins - support both legacy and template-based
    checkIns.slice(0, itemsPerSource).forEach(checkIn => {
      let title, icon;
      // Check if this is a template-based check-in (SUDS/Presence)
      if (checkIn.sudsRating != null || checkIn.presenceRating != null || checkIn.dynamicResponses?.length > 0) {
        if (checkIn.sudsRating != null) {
          title = `SUDS Check-in: ${checkIn.sudsRating}/10`;
          icon = checkIn.sudsRating <= 3 ? '😌' : checkIn.sudsRating <= 6 ? '😐' : '😰';
        } else if (checkIn.presenceRating != null) {
          title = `Presence Check-in: ${checkIn.presenceRating}/10`;
          icon = checkIn.presenceRating >= 7 ? '🧘' : '😐';
        } else {
          title = checkIn.templateName || 'Check-in';
          icon = '📋';
        }
      } else {
        // Legacy mood/anxiety check-in
        title = `Mood Check-in: ${checkIn.moodRating || 0}/5`;
        icon = '😊';
      }
      activities.push({
        type: 'checkin',
        title,
        timestamp: checkIn.createdAt,
        icon
      });
    });

    // Add recent goal updates
    goals.slice(0, itemsPerSource).forEach(goal => {
      if (goal.progress) {
        activities.push({
          type: 'goal',
          title: `Goal Progress: ${goal.title}`,
          timestamp: goal.updatedAt,
          icon: '🎯'
        });
      }
    });

    // Add recent journal entries
    journals.slice(0, itemsPerSource).forEach(entry => {
      activities.push({
        type: 'journal',
        title: entry.title || 'Journal Entry',
        timestamp: entry.createdAt,
        icon: '📝'
      });
    });

    // Sort by timestamp
    const sortedActivities = activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    // Return limited activities and total count
    return {
      items: sortedActivities.slice(0, limit),
      totalCount: sortedActivities.length,
      hasMore: sortedActivities.length > limit
    };
  };

  // Calculate weekly progress
  const calculateWeeklyProgress = (checkIns, goals, journals) => {
    // Filter data from the last 7 days
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    
    const thisWeekCheckIns = checkIns.filter(checkIn => {
      const checkInDate = new Date(checkIn.createdAt);
      return checkInDate > weekAgo;
    });
    
    const thisWeekJournals = journals.filter(journal => {
      const journalDate = new Date(journal.createdAt);
      return journalDate > weekAgo;
    });
    
    // Calculate goals progress (percentage of completed goals)
    const activeGoals = goals.filter(goal => goal.status !== 'archived');
    const completedGoals = activeGoals.filter(goal => goal.status === 'completed');
    const goalsProgress = activeGoals.length > 0 
      ? Math.round((completedGoals.length / activeGoals.length) * 100)
      : 0;

    // Calculate average based on check-in type
    // For template check-ins, use SUDS (lower is better) or Presence (higher is better)
    // For legacy check-ins, use mood rating
    let averageValue = 0;
    let averageLabel = 'Mood';
    let averageMax = 5;

    const templateCheckIns = thisWeekCheckIns.filter(c => c.sudsRating != null || c.presenceRating != null);
    const legacyCheckIns = thisWeekCheckIns.filter(c => c.moodRating != null);

    if (templateCheckIns.length > legacyCheckIns.length && templateCheckIns.length > 0) {
      // Use template-based values
      const sudsValues = templateCheckIns.map(c => c.sudsRating).filter(v => v != null);
      if (sudsValues.length > 0) {
        averageValue = sudsValues.reduce((s, v) => s + v, 0) / sudsValues.length;
        averageLabel = 'SUDS';
        averageMax = 10;
      } else {
        const presenceValues = templateCheckIns.map(c => c.presenceRating).filter(v => v != null);
        if (presenceValues.length > 0) {
          averageValue = presenceValues.reduce((s, v) => s + v, 0) / presenceValues.length;
          averageLabel = 'Presence';
          averageMax = 10;
        }
      }
    } else if (legacyCheckIns.length > 0) {
      // Use legacy mood values
      averageValue = legacyCheckIns.reduce((sum, c) => sum + (c.moodRating || 0), 0) / legacyCheckIns.length;
    }

    return {
      checkInsCount: thisWeekCheckIns.length,
      averageMood: averageValue,
      averageLabel: averageLabel,
      averageMax: averageMax,
      streak: calculateStreak(checkIns),
      goalsProgress: goalsProgress,
      journalCount: thisWeekJournals.length
    };
  };

  // Calculate streak
  const calculateStreak = (checkIns) => {
    if (!checkIns.length) return 0;
    
    let streak = 1;
    const sortedCheckIns = [...checkIns].sort((a, b) => 
      new Date(b.createdAt) - new Date(a.createdAt)
    );

    for (let i = 1; i < sortedCheckIns.length; i++) {
      const curr = new Date(sortedCheckIns[i].createdAt);
      const prev = new Date(sortedCheckIns[i - 1].createdAt);
      const diffDays = Math.floor((prev - curr) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 1) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  };

  // Handle quick action clicks
  const handleQuickAction = (action) => {
    switch (action) {
      case 'chat':
        navigate('/chat');
        break;
      case 'journal':
        navigate('/journal');
        break;
      case 'goals':
        // Open goals dialog directly in dashboard
        setGoalsDialogOpen(true);
        break;
      case 'progress':
        // Open progress dialog directly in dashboard
        setProgressDialogOpen(true);
        break;
      case 'session-summary':
        // Open session summary dialog directly in dashboard
        setSessionSummaryDialogOpen(true);
        break;
      case 'check-in-history':
        // Open check-in history dialog directly in dashboard
        setCheckInHistoryDialogOpen(true);
        break;
      default:
        navigate('/chat');
    }
  };

  // Handle check-in dialog
  const handleOpenCheckIn = () => {
    setCheckInDialogOpen(true);
  };

  const handleCloseCheckIn = () => {
    setCheckInDialogOpen(false);
  };

  const handleCheckInSuccess = () => {
    // Refetch dashboard data to show the new check-in
    fetchDashboardData();
  };

  // Extract fetchDashboardData to make it reusable
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch data in parallel for better performance
      const [checkIns, goals, journal, history] = await Promise.all([
        apiService.getCheckIns(token).catch(() => []),
        apiService.getGoals(token).catch(() => []),
        apiService.getJournalEntries(token).catch(() => ({ entries: [] })),
        apiService.getHistory(token).catch(() => null)
      ]);
      
      // Fetch session summary if there's an active conversation
      let sessionSummary = null;
      if (history?.conversationId) {
        try {
          sessionSummary = await apiService.getClientSessionSummary(
            history.conversationId,
            token
          );
          console.log('Session summary fetched:', sessionSummary);
        } catch (err) {
          // No summary available yet, which is fine
          console.log('No session summary available yet:', err.message);
        }
      } else {
        console.log('No conversation ID found in history:', history);
      }

      // Process and combine data
      const newDashboardData = {
        lastCheckIn: checkIns?.[0] || null,
        goals: goals || [],
        recentActivity: combineRecentActivity(
          checkIns || [],
          goals || [],
          Array.isArray(journal) ? journal : (journal?.entries || [])
        ).items,
        recentActivityFull: combineRecentActivity(
          checkIns || [],
          goals || [],
          Array.isArray(journal) ? journal : (journal?.entries || []),
          20  // Max 20 items in expanded view
        ),
        weeklyProgress: calculateWeeklyProgress(
          checkIns || [], 
          goals || [], 
          Array.isArray(journal) ? journal : (journal?.entries || [])
        ),
        checkIns: checkIns || [],
        lastSession: sessionSummary
      };

      setDashboardData(newDashboardData);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Unable to load dashboard data. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard data on mount and check onboarding
  useEffect(() => {
    if (token) {
      // Check if user needs onboarding
      if (!user?.isOnboarded) {
        setShowOnboarding(true);
      }
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  // Listen for real-time updates via socket
  useEffect(() => {
    if (!socket) return;

    const handleNewCheckIn = () => {
      // Refresh dashboard data when a new check-in is created
      fetchDashboardData();
    };

    const handleGoalUpdate = () => {
      // Refresh dashboard data when goals are updated
      fetchDashboardData();
    };

    const handleJournalUpdate = () => {
      // Refresh dashboard data when journal entries are updated
      fetchDashboardData();
    };

    // Subscribe to socket events
    socket.on('checkin:created', handleNewCheckIn);
    socket.on('goal:updated', handleGoalUpdate);
    socket.on('journal:created', handleJournalUpdate);

    // Cleanup
    return () => {
      socket.off('checkin:created', handleNewCheckIn);
      socket.off('goal:updated', handleGoalUpdate);
      socket.off('journal:created', handleJournalUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: dashboardTheme.colors.background.gradient,
      // The app's page container already clears the fixed header, so the extra 64px only
      // left an empty strip above the first card (phones: ST-20261007-0001; desktop: -0003).
      pt: { xs: 2, md: 3 },
      pb: { xs: 2, md: 3 }
    }}>
      {/* The app's own page container already pads 16px each side; this inner one doubled
          it, so on a phone every card started 32px in (ST-20261007-0001). No inner gutter
          on phones. */}
      <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2, md: 3 } }}>
        {/* Space reserved for future notices/alerts carousel */}
        <Box sx={{ 
          minHeight: { xs: '0px', md: '8px' },  // Small gap for potential future notices
          mb: 1
        }}>
          {/* Future: Add notification carousel/banner here when needed */}
        </Box>

        {/* Main dashboard. On desktop, three columns where each card keeps its own height:
            a shared row made every card as tall as Quick Actions, so the compact check-in
            became a large empty block (ST-20261007-0003). On phones the column wrappers
            dissolve (display: contents) and `order` keeps the original single-column order:
            check-in, quick actions, activity, progress, goals. */}
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' }, alignItems: 'start' }}>
          <Box sx={dashboardColumnSx}>
            <Box sx={{ order: 1 }}>
              <ErrorBoundary>
                <CompactCheckInWidget
                  lastCheckIn={dashboardData.lastCheckIn}
                  onCheckIn={handleOpenCheckIn}
                />
              </ErrorBoundary>
            </Box>
            <Box sx={{ order: 4 }}>
              <ErrorBoundary>
                <WeeklyProgress progress={dashboardData.weeklyProgress} />
              </ErrorBoundary>
            </Box>
          </Box>

          <Box sx={dashboardColumnSx}>
            <Box sx={{ order: 2 }}>
              <ErrorBoundary>
                <QuickActionsGrid onActionClick={handleQuickAction} />
              </ErrorBoundary>
            </Box>
            <Box sx={{ order: 5 }}>
              <ErrorBoundary>
                <GoalsSummary
                  goals={dashboardData.goals}
                  onManageGoals={() => setGoalsDialogOpen(true)}
                />
              </ErrorBoundary>
            </Box>
          </Box>

          <Box sx={dashboardColumnSx}>
            <Box sx={{ order: 3 }}>
              <ErrorBoundary>
                <ActivityFeed
                  activities={dashboardData.recentActivity}
                  fullActivityData={dashboardData.recentActivityFull}
                />
              </ErrorBoundary>
            </Box>
          </Box>
        </Box>

        {/* Check-in history and therapist summary: side by side from md, equal height. */}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <ErrorBoundary>
              <CheckInHistoryWidget
                checkIns={dashboardData.checkIns}
                onViewHistory={() => setCheckInHistoryDialogOpen(true)}
              />
            </ErrorBoundary>
          </Grid>
          <Grid item xs={12} md={6}>
            <ErrorBoundary>
              <SessionSummaryWidget
                lastSession={dashboardData.lastSession}
                onViewSummary={() => setSessionSummaryDialogOpen(true)}
              />
            </ErrorBoundary>
          </Grid>
        </Grid>
      </Container>
      
      {/* Check-in Dialog */}
      <CheckInDialog 
        open={checkInDialogOpen}
        onClose={handleCloseCheckIn}
        onSuccess={handleCheckInSuccess}
        token={token}
      />
      
      {/* Goals Dialog */}
      <GoalsDialog
        open={goalsDialogOpen}
        onClose={() => {
          setGoalsDialogOpen(false);
          // Refresh dashboard data to show updated goals
          fetchDashboardData();
        }}
        token={token}
      />
      
      {/* Progress Dialog */}
      <ProgressDialog
        open={progressDialogOpen}
        onClose={() => setProgressDialogOpen(false)}
        token={token}
      />
      
      {/* Session Summary Dialog */}
      <SessionSummaryDialog
        open={sessionSummaryDialogOpen}
        onClose={() => setSessionSummaryDialogOpen(false)}
        token={token}
      />
      
      {/* Check-in History Dialog */}
      <CheckInHistoryDialog
        open={checkInHistoryDialogOpen}
        onClose={() => setCheckInHistoryDialogOpen(false)}
        token={token}
      />
      
      {/* Onboarding Dialog */}
      <OnboardingDialog
        open={showOnboarding}
        userName={user?.name}
      />

    </Box>
  );
};

export default DashboardV2;